import { MouseEvent } from 'react';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import chevronRightIconTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { css } from '@emotion/css';
import { formatDistanceToNow } from 'date-fns';
import { ServiceMetadata } from '../api/services';
import { createTransition, fadeUpEntrance, reducedMotionQuery, staggerDelay } from '../theme/motion';
import useSurfaceStyles from '../hooks/useSurfaceStyles';

interface ServiceTableProps {
  services: ServiceMetadata[];
  onNavigate: (href: string) => void;
}

interface ServiceCardProps {
  service: ServiceMetadata;
  position: number;
  onNavigate: (href: string) => void;
}

function serviceHref(serviceName: string): string {
  return `/services/${encodeURIComponent(serviceName)}`;
}

function formatServiceMetadata(service: ServiceMetadata): string {
  const lastModified = service.modifiedAt
    ? `Updated ${formatDistanceToNow(service.modifiedAt, { addSuffix: true })}`
    : undefined;
  return [service.createdBy, lastModified].filter(Boolean).join(' · ');
}

function isModifiedClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

function ServiceCard({ service, position, onNavigate }: ServiceCardProps) {
  const meridianTheme = useMeridianTheme();
  const { interactiveSurfaceClass } = useSurfaceStyles();
  const href = serviceHref(service.serviceName);
  const apiCount = service.apiList?.length ?? 0;
  const metadata = formatServiceMetadata(service);

  const chevronClass = css({
    display: 'flex',
    color: meridianTheme('themeInkSubdued'),
    transition: createTransition(['transform', 'color']),
    [reducedMotionQuery]: { transition: 'none' },
  });
  const cardClass = css({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 16,
    padding: '16px 20px',
    color: 'inherit',
    textDecoration: 'none',
    animation: fadeUpEntrance,
    [`&:hover .${chevronClass}`]: {
      color: meridianTheme('themeActionPrimaryDefault'),
      transform: 'translateX(3px)',
    },
    [reducedMotionQuery]: { animation: 'none' },
  });
  const trailingClass = css({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  });

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isModifiedClick(event)) return;
    event.preventDefault();
    onNavigate(href);
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className={`${interactiveSurfaceClass} ${cardClass}`}
      style={{ animationDelay: staggerDelay(position) }}
    >
      <Column spacing="100" minWidth={0}>
        <Text type="h100" truncate>
          {service.serviceName}
        </Text>
        <Text type="b200" color="secondary" truncate>
          {service.namespace || 'No namespace'}
        </Text>
        {metadata && (
          <Text type="b100" color="secondary" truncate>
            {metadata}
          </Text>
        )}
      </Column>
      <div className={trailingClass}>
        <Tag type="theme">{`${apiCount} API${apiCount === 1 ? '' : 's'}`}</Tag>
        <span className={chevronClass}>
          <Icon tokens={chevronRightIconTokens}>View service</Icon>
        </span>
      </div>
    </a>
  );
}

export default function ServiceTable({ services, onNavigate }: ServiceTableProps) {
  return (
    <Column spacing="300" width="100%">
      {services.map((service, position) => (
        <ServiceCard key={service.serviceName} service={service} position={position} onNavigate={onNavigate} />
      ))}
    </Column>
  );
}
